export const XERO_AUTHORIZE_URL = 'https://login.xero.com/identity/connect/authorize'
export const XERO_TOKEN_URL = 'https://identity.xero.com/connect/token'
export const XERO_CONNECTIONS_URL = 'https://api.xero.com/connections'
export const XERO_API_BASE = 'https://api.xero.com/api.xro/2.0'

/** Granular Accounting API scopes for new Xero apps (from 2 March 2026). */
export const XERO_OAUTH_SCOPES = [
  'offline_access',
  'accounting.invoices',
  'accounting.payments',
  'accounting.contacts',
  'accounting.settings.read',
] as const

export const XERO_REQUIRED_SCOPES = XERO_OAUTH_SCOPES.join(' ')

export const XERO_CONNECTION_STATUSES = [
  'setup_required',
  'not_connected',
  'pending_tenant',
  'accounts_required',
  'connected',
  'disconnected',
] as const

export type XeroConnectionStatus = (typeof XERO_CONNECTION_STATUSES)[number]
export type XeroAuthMode = 'oauth' | 'custom_connection'
export type XeroOrganisationClassification = 'demo' | 'live' | 'unknown'

export interface XeroAuthorisedTenant {
  tenantId: string
  tenantName: string
  tenantType?: string
}

export interface XeroAccountOption {
  code: string
  name: string
  type: string
  accountClass?: string
  status?: string
  enablePaymentsToAccount?: boolean
}

export interface PublicXeroConnection {
  status: XeroConnectionStatus
  authenticationMode?: XeroAuthMode
  organisationName?: string
  organisationClassification?: XeroOrganisationClassification
  isDemoCompany?: boolean | null
  writesAllowed?: boolean
  tenantId?: string
  salesAccount?: XeroAccountOption | null
  depositAccount?: XeroAccountOption | null
  squareClearingAccount?: XeroAccountOption | null
  authorisedTenants?: XeroAuthorisedTenant[]
  salesAccounts?: XeroAccountOption[]
  bankAccounts?: XeroAccountOption[]
  connectedAt?: string | null
  scopes?: string
  setupRequired?: boolean
  missing?: string[]
  instructions?: string
  lastError?: string | null
}

export function xeroOAuthSetupInstructions() {
  return [
    'Create a Xero OAuth 2.0 app (standard web app), not a paid Custom Connection.',
    'Redirect URI must point at the xero-oauth-callback Edge Function.',
    `Request scopes: ${XERO_REQUIRED_SCOPES}.`,
    'Store XERO_CLIENT_ID, XERO_CLIENT_SECRET, and XERO_REDIRECT_URI in Supabase Edge Function secrets.',
    'An admin then uses Connect Xero so Lyle can sign in and authorise the organisation.',
    'Select a REVENUE/SALES account, a BANK Stripe clearing account, and a BANK Square clearing account before final settlement.',
    'Invoice writes to non-demo organisations stay blocked unless XERO_ALLOW_LIVE_WRITES=true.',
  ].join(' ')
}

export function classifyXeroOrganisation(isDemoCompany: boolean | null | undefined) {
  if (isDemoCompany === true) return 'demo' as const
  if (isDemoCompany === false) return 'live' as const
  return 'unknown' as const
}

export function canWriteXeroInvoices(input: {
  organisationClassification: XeroOrganisationClassification
  allowLiveWrites: boolean
}) {
  if (input.organisationClassification === 'demo') return true
  return input.allowLiveWrites === true
}

export function canCreateXeroInvoiceFromAdmin(connection: PublicXeroConnection | null | undefined) {
  if (!connection || connection.status !== 'connected') return false
  return connection.writesAllowed !== false
}

export function isActiveXeroAccount(account: Pick<XeroAccountOption, 'status'>) {
  return !account.status || account.status.toUpperCase() === 'ACTIVE'
}

export function isSalesRevenueAccount(account: Pick<XeroAccountOption, 'type' | 'accountClass'>) {
  const type = account.type.toUpperCase()
  const accountClass = (account.accountClass ?? '').toUpperCase()
  return type === 'REVENUE' || type === 'SALES' || accountClass === 'REVENUE'
}

export function isBankAccount(account: Pick<XeroAccountOption, 'type'>) {
  return account.type.toUpperCase() === 'BANK'
}

export function salesAccountOptions(accounts: XeroAccountOption[]) {
  return accounts.filter((account) => isActiveXeroAccount(account) && isSalesRevenueAccount(account))
}

export function bankAccountOptions(accounts: XeroAccountOption[]) {
  return accounts.filter((account) => isActiveXeroAccount(account) && isBankAccount(account))
}

export function assertSalesAccountCode(accounts: XeroAccountOption[], code: string) {
  const match = salesAccountOptions(accounts).find((account) => account.code === code)
  if (!match) {
    return { ok: false as const, error: 'Choose a Xero sales/revenue account for the job invoice.' }
  }
  return { ok: true as const, account: match }
}

export function assertBankAccountCode(accounts: XeroAccountOption[], code: string) {
  const match = bankAccountOptions(accounts).find((account) => account.code === code)
  if (!match) {
    return {
      ok: false as const,
      error: 'Choose a Xero BANK account for the Stripe clearing/deposit payment.',
    }
  }
  return { ok: true as const, account: match }
}

export function publicConnectionFromUnknown(value: unknown): PublicXeroConnection | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const tokenKeys = [
    'refresh_token',
    'refreshToken',
    'access_token',
    'accessToken',
    'refresh_token_ciphertext',
    'access_token_ciphertext',
    'client_secret',
    'clientSecret',
    'code_verifier',
    'codeVerifier',
  ]
  if (tokenKeys.some((key) => key in record && record[key])) return null
  if (typeof record.status !== 'string') return null
  return record as unknown as PublicXeroConnection
}

export function bytesToBase64Url(bytes: Uint8Array) {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

export function randomUrlToken(byteLength = 32) {
  return bytesToBase64Url(crypto.getRandomValues(new Uint8Array(byteLength)))
}

export async function sha256Base64Url(value: string) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return bytesToBase64Url(new Uint8Array(digest))
}

export async function createPkcePair() {
  const verifier = randomUrlToken(32)
  return {
    verifier,
    challenge: await sha256Base64Url(verifier),
    method: 'S256' as const,
  }
}

export function buildXeroAuthorizeUrl(input: {
  clientId: string
  redirectUri: string
  state: string
  codeChallenge: string
  scopes?: string
}) {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: input.clientId,
    redirect_uri: input.redirectUri,
    scope: input.scopes ?? XERO_REQUIRED_SCOPES,
    state: input.state,
    code_challenge: input.codeChallenge,
    code_challenge_method: 'S256',
  })
  return `${XERO_AUTHORIZE_URL}?${params.toString()}`
}

export async function encryptSecret(plaintext: string, secret: string) {
  const key = await aesKey(secret)
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(plaintext),
  )
  return `v1.${bytesToBase64Url(iv)}.${bytesToBase64Url(new Uint8Array(ciphertext))}`
}

export async function decryptSecret(payload: string, secret: string) {
  const [version, ivPart, dataPart] = payload.split('.')
  if (version !== 'v1' || !ivPart || !dataPart) {
    throw new Error('Xero token payload is not a recognised ciphertext.')
  }
  const key = await aesKey(secret)
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: base64UrlToBytes(ivPart) },
    key,
    base64UrlToBytes(dataPart),
  )
  return new TextDecoder().decode(plaintext)
}

async function aesKey(secret: string) {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(`autoglow-xero-token-v1:${secret}`),
  )
  return crypto.subtle.importKey('raw', digest, 'AES-GCM', false, ['encrypt', 'decrypt'])
}

function base64UrlToBytes(value: string) {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='))
  return Uint8Array.from(binary, (char) => char.charCodeAt(0))
}
